import React, { useState, useEffect, useRef } from 'react';

/**
 * Searchable item dropdown for the line items table.
 *
 * Before: embedded inside InvoiceModal.js as a ~130-line sub-component.
 * Now standalone and reusable.
 */
export default function SearchableItemSelect({ value, items, onSelect }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      const item = items.find((i) => i.item_id === value);
      setDisplayValue(item ? `${item.item_id}` : '');
      setSearchTerm('');
    } else {
      setDisplayValue('');
      setSearchTerm('');
    }
  }, [value, items]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const activeFilter = searchTerm.trim().toLowerCase();
  const filtered = activeFilter
    ? items.filter(
        (i) =>
          i.item_id?.toLowerCase().includes(activeFilter) ||
          i.sales_description?.toLowerCase().includes(activeFilter)
      )
    : items;

  const handleSelect = (item) => {
    setDisplayValue(`${item.item_id} - ${item.sales_description || ''}`);
    setSearchTerm('');
    setIsOpen(false);
    onSelect?.(item);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const inputValue = searchTerm !== '' ? searchTerm : displayValue;

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          className="w-full px-1 py-1 text-sm border border-transparent rounded focus:outline-none focus:ring-1 focus:ring-blue-500 pr-6"
        />
        <button
          type="button"
          onClick={() => { setIsOpen(!isOpen); if (!isOpen) setSearchTerm(''); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-500 text-[10px] p-0.5"
        >
          ▼
        </button>
      </div>

      {isOpen && (
        <div
          className="bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-y-auto z-[10000] mt-1"
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, minWidth: Math.max(dropdownPos.width, 250) }}
        >
          {filtered.length > 0 ? (
            filtered.map((item) => (
              <div
                key={item.item_id}
                onClick={() => handleSelect(item)}
                className="px-3 py-2.5 cursor-pointer border-b border-gray-100 hover:bg-gray-50"
              >
                <div className="text-sm text-gray-900">{item.item_id}</div>
                <div className="text-xs text-gray-500 mt-0.5">{item.sales_description}</div>
              </div>
            ))
          ) : (
            <div className="p-3 text-gray-500 text-sm">No items found</div>
          )}
        </div>
      )}
    </div>
  );
}
